import { SlidersHorizontalIcon } from '@phosphor-icons/react'
import { FC } from 'react'

import { TableColumn, TableColumnMenuContext } from '@/components/ui/table'
import { EMPTY_VALUE } from '@/constants'
import { popupFactory } from '@/factories'
import { PopupWithRequiredProps } from '@/factories/create-popup-factory'
import i18n from '@/i18n'
import { DisplayColumnFields } from '@/models/features/display-column.models'

import { ColumnDataSettingsModal, ColumnDataSettingsModalProps } from './column-data-settings-modal'
import { PersonCell } from './person-cell'
import { Person, PersonColumnOptions, PersonColumnSettings } from './person-column.types'
import { applyPersonDisplay, getPersonColumnWidth } from './person-column.utils'

// Note: a ui/table column showing one person per row (resident, staff member, or either) with the
// details picked in its "Column data settings" - see EL-15064. Pair with usePersonColumnSettings.
export const getPersonColumn = <T,>(
  settings: PersonColumnSettings,
  options: PersonColumnOptions<T>,
): TableColumn<T> => {
  const {
    id,
    title,
    pageName,
    source,
    getPerson,
    getDisplay,
    getLeadingDetail,
    renderFallback,
    renderExtra,
  } = options

  // Note: the table shows the BE's `<column>Display` values; the settings modal's preview renders
  // the raw person instead (`fromDisplay` false), so a field that's still off can be previewed.
  const renderCell = (row: T, fields: DisplayColumnFields, fromDisplay = true) => {
    const rawPerson = getPerson(row)
    if (!rawPerson) return renderFallback ? renderFallback(row) : EMPTY_VALUE
    const person =
      fromDisplay && getDisplay ? applyPersonDisplay(rawPerson, getDisplay(row)) : rawPerson

    return (
      <PersonCell
        person={person}
        fields={fields}
        fieldType={settings.fieldType}
        source={source}
        extra={renderExtra?.(row)}
        leadingDetail={getLeadingDetail?.(row)}
      />
    )
  }

  const openSettings = ({ columns, data }: TableColumnMenuContext<T>) => {
    const props: Omit<ColumnDataSettingsModalProps<T>, 'onClose'> = {
      columnId: id,
      columns,
      columnTitle: title,
      getPerson,
      pageName,
      renderCell: (row: T, fields: DisplayColumnFields) => renderCell(row, fields, false),
      renderPerson: (person: Person, fields: DisplayColumnFields) => (
        <PersonCell
          person={person}
          fields={fields}
          fieldType={settings.fieldType}
          source={source}
        />
      ),
      rows: data,
      settings,
    }
    popupFactory.open(ColumnDataSettingsModal as FC<PopupWithRequiredProps>, props)
  }

  return {
    // Note: EL-15428 - the header's "···" menu item carries a "New" pill, and the column picker
    // gets a "Settings" shortcut next to the column; both open the same modal.
    getMenuItems: settings.canEdit
      ? (context) => [
          {
            badge: i18n.t('person-column:menu.newBadge'),
            icon: SlidersHorizontalIcon,
            label: i18n.t('person-column:menu.columnDataSettings'),
            onClick: () => openSettings(context),
          },
        ]
      : undefined,
    getVisibilityAction: settings.canEdit
      ? (context) => ({
          icon: SlidersHorizontalIcon,
          label: i18n.t('person-column:menu.settings'),
          onClick: () => openSettings(context),
        })
      : undefined,
    hiddenByDefault: options.hiddenByDefault,
    highlighted: settings.highlighted,
    id,
    render: (row) => renderCell(row, settings.fields),
    sortAscLabel: options.sortAscLabel,
    sortDescLabel: options.sortDescLabel,
    sortKey: options.sortKey,
    title,
    tooltip: options.tooltip,
    width: getPersonColumnWidth(settings.fields, settings.fieldType, {
      hasLeadingDetail: !!getLeadingDetail,
    }),
  }
}
